import $ from 'jquery';
import EventEmitter from 'events';
import SyosApiDataService from './api/SyosApiDataService';
import SyosScreenContainer from './SyosScreenContainer';
import SyosSeatContainer from './SyosSeatContainer';
import SyosPriceTypeSelector from './SyosPriceTypeSelector';
import SyosSeatInfoPopover from './SyosSeatInfoPopover';
import SyosLegend from './SyosLegend';
import SyosCartContainer from './SyosCartContainer';
import SyosGlobalConfig, { StateClass } from './SyosGlobalConfig';
import SyosMobileCartControls from './SyosMobileCartControls';
import SyosBusyIndicator from './SyosBusyIndicator';
import SyosAjaxErrorModal from './modals/SyosAjaxErrorModal';
import SyosEvents from './utils/SyosEvents';
import SyosFullscreenManager from './utils/SyosFullscreenManager';
import SyosBtnViewScreens from './SyosBtnViewScreens';
import { toggleBodyClass } from './utils/html';
import { loadModal } from '../AsyncLoader';
import SyosPageComponent from './SyosPageComponent';
import getCurrencyFormatter from '../tnew/currency/getCurrencyFormatter';


const HTML = `
  <div class="tn-syos-seat-info-popover"></div>
  <div class="tn-syos-price-type-selector tn-syos-mobile-popover"></div>

  <div class="tn-syos__layout-container">
    <div class="tn-syos-busy-indicator"></div>

    <div class="tn-syos__screen-view-components">
      <div class="tn-syos-legend__container" style="display: none;"></div>
      <div class="tn-syos-screen-container"></div>
      <div class="tn-syos-btn-view-screens__container"></div>
      <div class="tn-syos-seat-container"></div>
    </div>

    <div class="tn-syos-mobile-cart-controls"></div>
    <div class="tn-syos-cart tn-syos-mobile-popover" style="display: none;"></div>
  </div>
`;


class SyosController extends EventEmitter {
  constructor(performanceId, screenId) {
    super();
    this.bindMethods();

    this.performanceId = performanceId;
    this.screenId = screenId;

    this.currentScreen = null;
    this.maximumSeatCount = 0;

    this.$el = $('.tn-syos');
    this.$el.html(HTML);

    this.init();
  }

  async init() {
    const currencyFormatter = await getCurrencyFormatter();

    this.fullscreenManager = new SyosFullscreenManager(this, this.$el[0]);
    this.dataService = new SyosApiDataService(this, currencyFormatter);
    this.legend = new SyosLegend(this, currencyFormatter);
    this.cartContainer = new SyosCartContainer(this, currencyFormatter);
    this.priceTypeSelector = new SyosPriceTypeSelector(this);
    this.seatInfoPopover = new SyosSeatInfoPopover(this);
    this.seatContainer = new SyosSeatContainer(this);
    this.busyIndicator = new SyosBusyIndicator(this);
    this.mobileCartControls = new SyosMobileCartControls(this, currencyFormatter);
    this.btnViewScreens = new SyosBtnViewScreens(this);

    this.screenContainer = new SyosScreenContainer(this);

    if (this.fullscreenManager.getFullscreenEnabled()) {
      this.$el.addClass(StateClass.FULL_SCREEN_ENABLED);
    }

    this.on(SyosEvents.CART_UPDATE, this.onCartUpdate);
    this.on(SyosEvents.RESERVE_SEATS, this.reserveTempCartSeats);
    this.on(SyosEvents.API_REQUEST_ERROR, () => SyosAjaxErrorModal.open(this));

    this.on(SyosEvents.LEGEND_DETAILS_SHOW, () => this.onLegendShow());
    this.on(SyosEvents.LEGEND_DETAILS_HIDE, () => this.onLegendHide());

    this.load();

    loadModal();
  }

  bindMethods() {
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.reserveTempCartSeats = this.reserveTempCartSeats.bind(this);
  }

  find(selector) {
    return this.$el.find(selector);
  }

  append($el) {
    return this.$el.append($el);
  }

  getDataService() { return this.dataService; }
  getCartContainer() { return this.cartContainer; }
  getSeatContainer() { return this.seatContainer; }

  getIsUsingCustomBirdsEye() {
    return this.screenContainer.getIsUsingCustomBirdsEye();
  }

  getMaximumSeatCount() {
    return this.maximumSeatCount;
  }

  toggleFullscreen() {
    this.fullscreenManager.toggleFullscreen();
  }

  getPerformanceId() {
    return this.performanceId;
  }

  getScreenId() {
    return this.screenId;
  }

  onCartUpdate() {
    const seatCount = this.cartContainer.getSeatCount();
    this.$el.toggleClass(StateClass.WITH_SEATS_SELECTED, seatCount !== 0);
  }

  onLegendHide() {
    this.$el.removeClass(StateClass.LEGEND_DETAILS_VISIBLE);
  }

  onLegendShow() {
    this.$el.addClass(StateClass.LEGEND_DETAILS_VISIBLE);
  }

  async load() {
    const perfNo = this.getPerformanceId();

    const perfData = await this.dataService.getPerformanceDetails(perfNo);

    if (!perfData || perfData.AvailablePrices.length === 0) {
      window.location.href = `${SyosGlobalConfig.bestAvailableBasePath}?z=0`;

      return;
    }

    this.maximumSeatCount = perfData.MaxSeats || 0; // TODO confirm 0 - switch to Infinity?

    const screensData = await this.dataService.getScreens(perfNo);

    this.screenContainer.setData(screensData);
  }

  getCurrentScreen() { return this.currentScreen; }

  setCurrentScreen(screen) {
    if (screen === this.currentScreen) {
      return;
    }

    this.currentScreen = screen;
    toggleBodyClass(StateClass.VIEWING_SEATS, this.currentScreen !== null);

    this.emit(SyosEvents.SCREEN_UPDATED, { screen });
  }

  unsetCurrentScreen() {
    this.setCurrentScreen(null);
  }

  hideSeatContainer() {
    if (this.seatContainer) {
      this.seatContainer.hide();
    }
  }

  async reserveTempCartSeats() {
    const seats = this.cartContainer.getSeats();

    if (seats.size === 0) {
      return;
    }

    this.emit(SyosEvents.API_RESERVION_ATTEMPT);

    const reservationData = [];

    seats.forEach(((seat) => {
      const zoneId = seat.zoneId;
      const priceTypeId = seat.selectedPriceTypeId;
      const id = seat.id;

      reservationData.push({
        PerfNo: this.performanceId,
        PriceType: priceTypeId,
        ZoneNo: zoneId,
        SeatNo: id,
      });
    }));

    const data = await this.dataService.reserveTempCartSeats(reservationData);

    const numberRequested = data.num_seats_requested;
    const numberReserved = data.num_seats_reserved;

    if (numberReserved === numberRequested) {
      this.emit(SyosEvents.API_RESERVATION_SUCCESS);
      window.location = SyosGlobalConfig.cartPath.replace('{lineItemId}', data.lineitemId);
    } else {
      this.emit(SyosEvents.API_RESERVATION_FAIL);

      let errorMessage = null;

      if (data.HasError && data.ErrorMessage) {
        errorMessage = `<p>${data.ErrorMessage}</p>`;
      } else if (numberReserved !== numberRequested) {
        errorMessage = '<p>The seats that you requested could not be reserved.</p>';
      } else {
        errorMessage = '<p>An unknown error has occurred</p>';
      }

      if (this.currentScreen) {
        errorMessage += '<p>Seat availability is being refreshed.</p>';
      }

      if (errorMessage !== null) {
        loadModal().then((Modal) => {
          Modal.alert({
            root: this.$el,
            title: SyosPageComponent.reservationFailureModalTitle,
            bodyContents: errorMessage,
          });
        });

        this.cartContainer.empty();

        // TODO this checked for FaultType === 1 before
        if (this.currentScreen) {
          this.seatContainer.load(
            this.currentScreen.perf_no,
            this.currentScreen.facility_no,
            this.currentScreen.screen_no,
          );
        }
      }
    }
  }
}

export default SyosController;



// WEBPACK FOOTER //
// ./src/syos/SyosController.js